/**
 * SakshamScreen.jsx — Mobile AI Chat (Phase 3)
 *
 * ALL logic preserved exactly from Chatbot.jsx:
 *  - 5 msg/day free limit (server-enforced + client mirror)
 *  - Rolling 6-message window (free), summary memory (paid)
 *  - Token guard (max ~375 words per message)
 *  - Speech recognition
 *  - Crisis detection + helpline CTA
 *  - sessionStorage + Firestore memory for paid users
 *
 * ONLY the UI/CSS has changed. Every handler, API call, and state
 * variable is identical to the original Chatbot.jsx.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { authFetch, getUserClaims } from "../../utils/authFetch";
import { useAppShell } from "../AppShellContext";

const FREE_MSG_LIMIT   = 10;
const FREE_TOKEN_LIMIT = 600;
const FREE_WORD_LIMIT  = Math.floor(FREE_TOKEN_LIMIT * 0.75);
const MAX_WORDS        = 375;

// ── Quick action chips shown after AI responses ──
const QUICK_ACTIONS = [
  { id: "breathe",  emoji: "🌬️", label: "Breathing",  action: "tab", target: "explore" },
  { id: "talk",     emoji: "💬", label: "Keep talking", action: "focus" },
  { id: "assess",   emoji: "📊", label: "Assessment",  action: "tab", target: "explore" },
];

export default function SakshamScreen() {
  const navigate = useNavigate();
  const { setActiveTab } = useAppShell();

  // ── Chat state (IDENTICAL to Chatbot.jsx) ──
  const [messages, setMessages] = useState([{
    text: "Hi there! 💙 I'm Saksham, your mental wellness companion from HealHub. I'm here to listen, support, and walk alongside you. How are you feeling today?",
    sender: "bot", time: new Date(),
  }]);
  const [inputText, setInputText]       = useState("");
  const [loading, setLoading]           = useState(false);
  const [isListening, setIsListening]   = useState(false);

  // ── Memory (IDENTICAL to Chatbot.jsx) ──
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionSummary, setSessionSummary] = useState(
    sessionStorage.getItem("saksham_summary") || ""
  );
  const [memoryLoaded, setMemoryLoaded] = useState(false);

  // ── Usage/limits (IDENTICAL to Chatbot.jsx) ──
  const [msgUsed, setMsgUsed]           = useState(0);
  const [tokensUsed, setTokensUsed]     = useState(0);
  const [isPaid, setIsPaid]             = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [usageLoaded, setUsageLoaded]   = useState(false);

  // ── Refs ──
  const recognitionRef = useRef(null);
  const chatEndRef     = useRef(null);
  const inputRef       = useRef(null);

  // ── Init: load usage + memory (IDENTICAL to Chatbot.jsx) ──
  useEffect(() => {
    const load = async () => {
      try {
        const claims = await getUserClaims();
        setIsPaid(claims.paid);
        const res  = await authFetch("/api/usage");
        const data = await res.json();
        const msgs   = data.chat?.msgsUsed   || 0;
        const tokens = data.chat?.tokensUsed || 0;
        setMsgUsed(msgs);
        setTokensUsed(tokens);
        if (!claims.paid && (msgs >= FREE_MSG_LIMIT || tokens >= FREE_TOKEN_LIMIT)) {
          setLimitReached(true);
        }
        if (claims.paid && !sessionStorage.getItem("saksham_summary")) {
          try {
            const memRes  = await authFetch("/api/chat/memory");
            const memData = await memRes.json();
            if (memData.summary) {
              setSessionSummary(memData.summary);
              sessionStorage.setItem("saksham_summary", memData.summary);
            }
          } catch { /* non-critical */ }
        }
      } catch { /* silent */ }
      finally { setUsageLoaded(true); setMemoryLoaded(true); }
    };
    if (auth.currentUser) load();
  }, []);

  // ── Scroll to bottom on new message ──
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Speech recognition (IDENTICAL to Chatbot.jsx) ──
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (SpeechRecognition && !recognitionRef.current) recognitionRef.current = new SpeechRecognition();

  useEffect(() => {
    if (!recognitionRef.current) return;
    navigator.mediaDevices.getUserMedia({ audio: true }).then(() => {
      const r = recognitionRef.current;
      r.continuous = true; r.interimResults = true; r.lang = "en-IN";
      r.onresult = (e) => setInputText(Array.from(e.results).map(res => res[0].transcript).join(""));
    }).catch(() => {});
  }, []);

  const toggleListening = () => {
    const r = recognitionRef.current; if (!r) return;
    isListening ? r.stop() : r.start();
    setIsListening(p => !p);
  };

  // ── Derived ──
  const wordCount       = inputText.trim().split(/\s+/).filter(Boolean).length;
  const msgTooLong      = wordCount > MAX_WORDS;
  const msgsRemaining   = Math.max(0, FREE_MSG_LIMIT - msgUsed);
  const currentMsgTokens = Math.ceil((inputText || "").length / 4);

  // ── Send message (IDENTICAL logic to Chatbot.jsx) ──
  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim() || loading) return;

    if (!auth.currentUser) {
      setMessages(p => [...p, { text: "Please log in to chat with Saksham. 💙", sender: "bot", time: new Date() }]);
      return;
    }
    if (limitReached && !isPaid) {
      setMessages(p => [...p, {
        text: `You've used all ${FREE_MSG_LIMIT} free messages today. Come back tomorrow, or upgrade for unlimited conversations. 💙`,
        sender: "bot", time: new Date(),
      }]);
      return;
    }
    if (msgTooLong) {
      setMessages(p => [...p, {
        text: `Your message is a bit long (${wordCount} words). Please keep it under ${MAX_WORDS} words so I can respond thoughtfully. 💙`,
        sender: "bot", time: new Date(),
      }]);
      return;
    }

    const userMsg = { text: inputText, sender: "user", time: new Date() };
    setMessages(p => [...p, userMsg]);
    const msgToSend = inputText;
    setInputText("");
    setLoading(true);

    const apiHistory = sessionHistory.slice(-6).map(m => ({
      role: m.sender === "user" ? "user" : "assistant",
      content: m.text,
    }));

    try {
      const res  = await authFetch("/api/chat", {
        method: "POST",
        body: JSON.stringify({ message: msgToSend, history: apiHistory, sessionSummary }),
      });
      const data = await res.json();

      if (res.status === 429) {
        setLimitReached(true);
        setMsgUsed(FREE_MSG_LIMIT);
        setMessages(p => [...p, {
          text: data.error || `You've used today's ${FREE_MSG_LIMIT} free messages. Upgrade to continue. 💙`,
          sender: "bot", time: new Date(),
        }]);
        return;
      }
      if (!res.ok) {
        setMessages(p => [...p, { text: data.error || "Something went wrong. Please try again.", sender: "bot", time: new Date() }]);
        return;
      }

      const botMsg = { text: data.message, sender: "bot", isCrisis: data.isCrisis, time: new Date() };
      setMessages(p => [...p, botMsg]);
      setSessionHistory(p => [...p, userMsg, botMsg].slice(-12));

      if (data.sessionSummary) {
        setSessionSummary(data.sessionSummary);
        sessionStorage.setItem("saksham_summary", data.sessionSummary);
      }
      if (!isPaid) {
        const newMsgs   = msgUsed + 1;
        const newTokens = tokensUsed + currentMsgTokens;
        setMsgUsed(newMsgs);
        setTokensUsed(newTokens);
        if (newMsgs >= FREE_MSG_LIMIT || newTokens >= FREE_TOKEN_LIMIT) setLimitReached(true);
      }
    } catch {
      setMessages(p => [...p, { text: "Saksham is momentarily unavailable. Please try again. 💙", sender: "bot", time: new Date() }]);
    } finally {
      setLoading(false);
    }
  }, [inputText, loading, limitReached, isPaid, msgTooLong, wordCount, sessionHistory, sessionSummary, msgUsed, tokensUsed, currentMsgTokens]);

  // ── Quick action handler ──
  const handleQuickAction = useCallback((action) => {
    if (action.action === "tab") setActiveTab(action.target);
    else if (action.action === "navigate") navigate(action.target);
    else if (action.action === "focus") inputRef.current?.focus();
  }, [setActiveTab, navigate]);

  // ── Format time ──
  const fmtTime = (d) => {
    if (!d) return "";
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="hh-saksham">

      {/* ═══ DARK HEADER ═══ */}
      <div className="hh-sak-header">
        <div className="hh-sak-status">
          <div className="hh-sak-dot" />
          <span className="hh-sak-status-text">SAKSHAM AI — ONLINE</span>
        </div>
        <div className="hh-sak-title">Your AI Companion 🤖</div>
        <div className="hh-sak-sub">Safe · Private · Always here</div>

        {/* Usage pill */}
        {usageLoaded && (
          <div className={`hh-sak-plan ${isPaid ? "paid" : ""}`}>
            {isPaid
              ? "⭐ Plus — Unlimited"
              : `${msgsRemaining}/${FREE_MSG_LIMIT} messages left today`
            }
          </div>
        )}
      </div>

      {/* ═══ CHAT MESSAGES ═══ */}
      <div className="hh-sak-chat">
        {messages.map((m, i) => (
          <div key={i} className={`hh-sak-msg ${m.sender === "user" ? "user" : "ai"}`}>
            {m.sender === "bot" && (
              <div className="hh-sak-avatar">🤖</div>
            )}
            <div className="hh-sak-bubble-wrap">
              {m.sender === "bot" && (
                <div className="hh-sak-name">Saksham</div>
              )}
              <div className={`hh-sak-bubble ${m.sender === "user" ? "user" : "ai"}${m.isCrisis ? " crisis" : ""}`}>
                {m.text}
                {m.isCrisis && (
                  <a href="tel:9152987821" className="hh-sak-crisis-btn">
                    📞 iCall — 9152987821
                  </a>
                )}
              </div>
              <div className="hh-sak-time">{fmtTime(m.time)}</div>
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div className="hh-sak-msg ai">
            <div className="hh-sak-avatar">🤖</div>
            <div className="hh-sak-bubble-wrap">
              <div className="hh-sak-name">Saksham</div>
              <div className="hh-sak-bubble ai">
                <div className="hh-sak-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Limit reached banner */}
        {limitReached && !isPaid && (
          <div className="hh-sak-limit-banner">
            <div className="hh-sak-limit-text">
              You've used all {FREE_MSG_LIMIT} free messages today 💙
            </div>
            <button className="hh-sak-upgrade-btn" onClick={() => navigate("/pricing")}>
              Upgrade — ₹35/mo →
            </button>
          </div>
        )}

        {/* Quick action chips — show after last bot message */}
        {messages.length > 1 && messages[messages.length - 1].sender === "bot" && !loading && !limitReached && (
          <div className="hh-sak-actions">
            {QUICK_ACTIONS.map(a => (
              <button key={a.id} className="hh-sak-action-chip" onClick={() => handleQuickAction(a)}>
                {a.emoji} {a.label}
              </button>
            ))}
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* ═══ INPUT BAR ═══ */}
      <div className="hh-sak-input-bar">
        {/* Word count warning */}
        {msgTooLong && (
          <div className="hh-sak-warn">
            Message too long ({wordCount}/{MAX_WORDS} words)
          </div>
        )}

        <div className="hh-sak-input-row">
          {/* Mic button */}
          {recognitionRef.current && (
            <button
              className={`hh-sak-mic${isListening ? " on" : ""}`}
              onClick={toggleListening}
              aria-label={isListening ? "Stop listening" : "Voice input"}
            >
              {isListening ? "⏹" : "🎤"}
            </button>
          )}

          {/* Text input */}
          <div className="hh-sak-input-wrap">
            <input
              ref={inputRef}
              className="hh-sak-input"
              value={inputText}
              placeholder={limitReached && !isPaid ? "Come back tomorrow 💙" : "Talk to Saksham…"}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSendMessage()}
              disabled={limitReached && !isPaid}
              autoComplete="off"
            />
            {inputText && (
              <span className={`hh-sak-wc${msgTooLong ? " over" : ""}`}>
                {wordCount}w
              </span>
            )}
          </div>

          {/* Send button */}
          <button
            className="hh-sak-send"
            onClick={handleSendMessage}
            disabled={loading || (limitReached && !isPaid) || !inputText.trim() || msgTooLong}
            aria-label="Send message"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>

        {/* Disclaimer */}
        <div className="hh-sak-disclaimer">
          Saksham is an AI companion and may make mistakes. For serious concerns, consult a professional. Crisis: iCall 9152987821
        </div>
      </div>
    </div>
  );
}